// COGS — cost per unit per SKU, grouped by product type. Make-up differs by type: cans use the
// tolling tier table, spirit bottles use a per-bottle build (fill/bottle/cork/label), all + freight/
// COA. Click a row to deep-dive the full make-up.
import { Fragment, useMemo, useState } from "react";
import { computeCogs } from "@shared/engine.ts";
import { useData } from "../state/store.tsx";
import { SegTabs, InfoTip } from "../components/ui.tsx";
import { money, num } from "../lib/format.ts";

export default function Cogs() {
  const { data } = useData();
  const [tierIdx, setTierIdx] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  if (!data) return null;
  const cur = data.business.currency;
  const massUnit = data.business.massUnit || "lb";
  const unit = data.business.unitLabel;
  const tiers = data.business.cogsTierSizes;
  const activeSkus = data.skus.filter((s) => !s.discontinued && !s.assembled);

  const rows = useMemo(
    () => activeSkus.map((s) => ({ sku: s, cogs: computeCogs(data, s, tierIdx) })),
    [data, activeSkus, tierIdx],
  );
  // Group by product type, preserving first-seen order.
  const groups = useMemo(() => {
    const m = new Map<string, { label: string; rows: typeof rows }>();
    for (const r of rows) {
      const t = r.cogs.productType;
      if (!m.has(t.id)) m.set(t.id, { label: t.label, rows: [] });
      m.get(t.id)!.rows.push(r);
    }
    return Array.from(m.values());
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold">COGS</h2>
          <p className="text-muted text-xs mt-0.5">Cost per unit = ingredients (recipe × prices) + production + freight/COA. Make-up varies by product type. Click a row for the full breakdown.</p>
        </div>
        {tiers.length > 1 && (
          <div>
            <span className="label">Run size</span>
            <SegTabs tabs={tiers.map((t, i) => ({ id: String(i), label: num(t) }))} value={String(tierIdx)} onChange={(v) => setTierIdx(Number(v))} />
          </div>
        )}
      </div>

      {groups.map((g) => (
        <div key={g.label} className="card overflow-x-auto">
          <div className="text-sm font-semibold mb-2">{g.label}</div>
          <table className="w-full min-w-[680px]">
            <thead><tr>
              <th className="th">SKU</th>
              <th className="th text-right">Ingredient <InfoTip text={`Sum of (${massUnit}/unit × $/${massUnit}) across the recipe.`} /></th>
              <th className="th text-right">Production <InfoTip text="Cans: tolling fee at the selected run size. Bottles: per-bottle build (fill + bottle + cork + label). Set under Admin → Planning → Product types." /></th>
              <th className="th text-right">Freight + COA <InfoTip text="Inbound freight, COA/testing & other adders that apply to this product type (Admin → Planning → COGS extras). Per-run spread over the run size; per-pallet uses this type's units/pallet." /></th>
              <th className="th text-right">Total / unit</th>
              <th className="th w-6"></th>
            </tr></thead>
            <tbody>
              {g.rows.map(({ sku, cogs }) => {
                const isOpen = open === sku.id;
                return (
                  <Fragment key={sku.id}>
                    <tr className="cursor-pointer hover:bg-paper/60" onClick={() => setOpen(isOpen ? null : sku.id)}>
                      <td className="td font-medium">{sku.name}</td>
                      <td className="td text-right tabular-nums">{money(cogs.ingredientCostPerCan, cur)}</td>
                      <td className="td text-right tabular-nums text-muted">{cogs.productionCostPerCan ? money(cogs.productionCostPerCan, cur) : "—"}</td>
                      <td className="td text-right tabular-nums text-muted">{cogs.extrasPerCan ? money(cogs.extrasPerCan, cur) : "—"}</td>
                      <td className="td text-right tabular-nums font-semibold">{money(cogs.totalPerCan, cur)}</td>
                      <td className="td text-right text-muted">{isOpen ? "▾" : "▸"}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td className="td bg-paper/50" colSpan={6}>
                          <div className="grid sm:grid-cols-3 gap-4 py-1">
                            <BreakdownCol title={`Ingredients · ${money(cogs.ingredientCostPerCan, cur)}`}
                              lines={cogs.ingredientLines.map((l) => ({ label: `${l.ingredient} · ${l.lbsPerCan.toFixed(4)}${massUnit} × ${money(l.unitCost, cur)}`, val: money(l.costPerCan, cur) }))} />
                            <BreakdownCol title={`Production · ${money(cogs.productionCostPerCan, cur)}`}
                              lines={cogs.productionLines.map((l) => ({ label: l.name, val: money(l.perCan, cur) }))}
                              empty={cogs.productType.purchased ? "Purchased (no production)" : "No production cost set"} />
                            <BreakdownCol title={`Freight + COA · ${money(cogs.extrasPerCan, cur)}`}
                              lines={cogs.extraLines.map((l) => ({ label: l.name, val: money(l.perCan, cur) }))}
                              empty="No adders for this type" />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      <p className="text-xs text-muted">Edit ingredient prices, tolling tiers, product types (build costs, units/pallet) &amp; freight under <span className="font-medium">Admin &amp; Setup → Planning</span>. Unit = {unit} / bottle / etc. by product type.</p>
    </div>
  );
}

function BreakdownCol({ title, lines, empty }: { title: string; lines: { label: string; val: string }[]; empty?: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted font-medium mb-1">{title}</div>
      {lines.length === 0 ? <div className="text-xs text-muted/70">{empty || "—"}</div> : (
        <div className="space-y-0.5">
          {lines.map((l, i) => (
            <div key={i} className="flex justify-between gap-2 text-xs">
              <span className="text-muted">{l.label}</span>
              <span className="tabular-nums">{l.val}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
